// LBD-CUSTOM: Design tiles.
//
// Drives the hidden stock <variant-picker> inputs of the decoration option, rendered by
// snippets/lbd-design-tiles.liquid as a sibling of <variant-picker>, never inside it, so
// the picker's own `select option[selected], fieldset input:checked` queries never see
// these buttons.
//
// This element holds no state of its own beyond a one-shot correction flag. Everything it
// shows is re-read from the picker's server-rendered inputs on connect and after every
// `variant:update`, which Horizon dispatches once it has morphed the picker. A size or
// colour change therefore cannot desync it, and deep links work because Shopify checks the
// right input server-side.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} LbdDesignTilesRefs
 * @property {HTMLElement} [legendValue]
 * @property {HTMLElement} [note]
 */

/**
 * @extends {Component<LbdDesignTilesRefs>}
 */
class LbdDesignTiles extends Component {
  #abortController = new AbortController();

  /**
   * Set by a tile click, consumed by the next sync. Holds the colour that was selected at
   * click time so the note can name it. Null means no correction is pending.
   * @type {{ colour: string, design: string } | null}
   */
  #pendingFix = null;

  /**
   * Text to show once the correction's own morph has landed. Showing it before that would
   * flash it away, because the correction triggers another variant update.
   * @type {string | null}
   */
  #pendingNote = null;

  /** True while the note is on screen, so the next interaction clears it. */
  #noteShown = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#onClick, { signal });
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#sync, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // Fired by the morph when a whole section is re-rendered (combined listings, featured product).
  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  /** Same scope product-form-component listens on, so quick-add dialogs and cards behave alike. */
  get #scope() {
    return this.closest('.shopify-section, dialog, product-card') ?? document;
  }

  /** @returns {Element | null} */
  get #picker() {
    return this.#scope.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  /** The colour fieldset, located by the index the block passed in. @returns {Element | null} */
  get #colourFieldset() {
    const position = Number(this.dataset.colourPosition ?? 0);
    if (!position) return null;
    return this.#picker?.querySelector(`[data-lbd-colour][data-fieldset-index="${position - 1}"]`) ?? null;
  }

  /**
   * The stock input for an option value: a radio in buttons mode, an <option> in dropdowns mode.
   * @param {string} optionValueId
   * @returns {Element | null}
   */
  #input(optionValueId) {
    if (!optionValueId) return null;
    return this.#picker?.querySelector(`[data-option-value-id="${optionValueId}"]`) ?? null;
  }

  /** @param {Element | null} input */
  #isSelected(input) {
    if (input instanceof HTMLInputElement) return input.checked;
    if (input instanceof HTMLOptionElement) return input.selected;
    return false;
  }

  /**
   * Select an option value by driving the stock input and letting <variant-picker> do
   * everything else: cache update, fetch, morph, URL, events.
   * @param {Element | null} input
   */
  #selectInput(input) {
    if (!input || this.#isSelected(input)) return;

    if (input instanceof HTMLInputElement) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (input instanceof HTMLOptionElement) {
      const select = input.closest('select');
      if (!select) return;
      select.value = input.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest('[data-lbd-target]');
    if (!(tile instanceof HTMLElement) || !this.contains(tile)) return;

    const input = this.#input(tile.dataset.lbdTarget ?? '');
    if (!input || this.#isSelected(input)) return;

    // Remember what was selected so the next sync can tell whether this pick stranded the
    // colour, and can name it in the note.
    this.#pendingFix = {
      colour: this.#selectedColourName() ?? '',
      design: tile.dataset.lbdName ?? '',
    };
    this.#selectInput(input);
  };

  /** @returns {string | null} */
  #selectedColourName() {
    const checked = this.#colourFieldset?.querySelector('input:checked');
    return checked instanceof HTMLInputElement ? checked.value : null;
  }

  /**
   * A colour the selected design is not printed on comes back with no data-variant-id,
   * because Shopify resolves product_option_value.variant against the current selection.
   * Do not read data-option-available: it stays true when inventory is untracked.
   * @returns {boolean}
   */
  #selectedColourIsStranded() {
    const checked = this.#colourFieldset?.querySelector('input:checked');
    return checked instanceof HTMLInputElement && !checked.hasAttribute('data-variant-id');
  }

  /**
   * Move to the first colour the newly selected design is actually printed on. Runs at most
   * once per tile click, so it can never loop with the morph that follows it.
   */
  #applyPendingFix() {
    const fix = this.#pendingFix;
    this.#pendingFix = null;
    if (!fix || !this.#selectedColourIsStranded()) return;

    const replacement = this.#colourFieldset?.querySelector('input[data-variant-id]');
    if (!(replacement instanceof HTMLInputElement)) return;

    const template = this.refs.note?.dataset.template ?? '';
    if (template) {
      this.#pendingNote = template
        .replace('[colour]', fix.colour)
        .replace('[design]', fix.design)
        .replace('[fallback]', replacement.value);
    }
    this.#selectInput(replacement);
  }

  /**
   * The note describes one correction. It appears on the sync after that correction lands
   * and is cleared by the next interaction, so it can never sit there describing a colour
   * and design the shopper has already moved away from.
   */
  #updateNote() {
    const note = this.refs.note;
    if (!note) return;

    if (this.#pendingNote) {
      note.textContent = this.#pendingNote;
      note.hidden = false;
      this.#pendingNote = null;
      this.#noteShown = true;
      return;
    }

    if (this.#noteShown) {
      note.hidden = true;
      note.textContent = '';
      this.#noteShown = false;
    }
  }

  /** Mirror the picker's current selection onto the tiles and the legend. */
  #sync = () => {
    if (!this.#picker) return;

    let selected = null;
    for (const tile of this.querySelectorAll('[data-lbd-target]')) {
      if (!(tile instanceof HTMLElement)) continue;
      const isSelected = this.#isSelected(this.#input(tile.dataset.lbdTarget ?? ''));
      tile.setAttribute('aria-pressed', String(isSelected));
      if (isSelected) selected = tile;
    }

    const legend = this.refs.legendValue;
    if (legend) legend.textContent = selected?.dataset.lbdName ?? '';

    // Order matters: show or clear the note for the state we are in now, then queue any
    // correction this state calls for. Its note appears on the sync that correction causes.
    this.#updateNote();
    this.#applyPendingFix();
  };
}

if (!customElements.get('lbd-design-tiles')) {
  customElements.define('lbd-design-tiles', LbdDesignTiles);
}
